import React, { useState, useEffect } from 'react';
import { useComunicacao } from '../../contexts/ComunicacaoContext';
import { useUsuario } from '../../contexts/UsuarioContext';
import comunicacaoService from '../../services/comunicacaoService';
import ConfirmDialog from '../common/ConfirmDialog';
import { toast } from 'react-toastify';

interface ModalEditarCanalProps {
  canalId: string;
  onClose: () => void;
}

const ModalEditarCanal: React.FC<ModalEditarCanalProps> = ({ canalId, onClose }) => {
  const { editarCanal, removerCanal } = useComunicacao();
  const { usuarios, carregarUsuarios } = useUsuario();
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [membrosSelecionados, setMembrosSelecionados] = useState<string[]>([]);
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [modalConfirmacaoAberto, setModalConfirmacaoAberto] = useState(false);

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setCarregando(true);
        await carregarUsuarios();
        const canal = await comunicacaoService.obterCanal(canalId);
        setNome(canal.nome);
        setDescricao(canal.descricao || '');
        setMembrosSelecionados(canal.membros?.map(m => m.id) || []);
      } catch (error) {
        console.error('Erro ao carregar canal:', error);
      } finally {
        setCarregando(false);
      }
    };

    carregarDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canalId]);

  const usuariosFiltrados = usuarios.filter(u =>
    (u.nome?.toLowerCase() || '').includes(busca.toLowerCase()) ||
    (u.email?.toLowerCase() || '').includes(busca.toLowerCase())
  );

  const toggleMembro = (usuarioId: string) => {
    setMembrosSelecionados(prev =>
      prev.includes(usuarioId)
        ? prev.filter(id => id !== usuarioId)
        : [...prev, usuarioId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nome.trim()) {
      return;
    }

    if (membrosSelecionados.length === 0) {
      toast.error('Selecione pelo menos um membro para o canal');
      return;
    }

    try {
      setSalvando(true);
      await editarCanal(canalId, {
        nome: nome.trim(),
        descricao: descricao.trim(),
        membros: membrosSelecionados
      });
      onClose();
    } catch (error) {
      console.error('Erro ao editar canal:', error);
    } finally {
      setSalvando(false);
    }
  };

  const handleRemover = () => {
    setModalConfirmacaoAberto(true);
  };

  const confirmarRemocao = async () => {
    try {
      setSalvando(true);
      await removerCanal(canalId);
      setModalConfirmacaoAberto(false);
      onClose();
    } catch (error) {
      console.error('Erro ao remover canal:', error);
      setSalvando(false);
      setModalConfirmacaoAberto(false);
    }
  };

  if (carregando) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="flex items-center gap-3">
            <svg className="animate-spin h-6 w-6 text-bravo-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">Editar Canal</h2>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Canal *
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Gestores, Equipe de Vendas..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              placeholder="Descreva o propósito deste canal..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent resize-none"
            />
          </div>

          {/* Membros atuais */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Membros Atuais ({membrosSelecionados.length})
            </label>
            {membrosSelecionados.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Nenhum membro selecionado.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {usuarios
                  .filter(u => membrosSelecionados.includes(u.id))
                  .map(u => (
                    <div key={u.id} className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full pl-1 pr-2 py-1">
                      <img src={u.avatar || '/profilepicture.png'} alt={u.nome} className="w-6 h-6 rounded-full" />
                      <span className="text-sm font-medium text-blue-800">{u.nome}</span>
                      <button
                        type="button"
                        onClick={() => toggleMembro(u.id)}
                        className="text-blue-400 hover:text-red-500 transition-colors ml-1"
                        title="Remover membro"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Adicionar membros */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Adicionar Membros
            </label>
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar usuários..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-bravo-primary focus:border-transparent mb-3"
            />

            <div className="border border-gray-300 rounded-md max-h-48 overflow-y-auto">
              {usuariosFiltrados
                .filter(u => !membrosSelecionados.includes(u.id))
                .map((usuario) => (
                  <label
                    key={usuario.id}
                    className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={false}
                      onChange={() => toggleMembro(usuario.id)}
                      className="mr-3 h-4 w-4 text-bravo-primary focus:ring-bravo-primary border-gray-300 rounded"
                    />
                    <img
                      src={usuario.avatar || '/profilepicture.png'}
                      alt={usuario.nome}
                      className="w-8 h-8 rounded-full mr-3"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">{usuario.nome}</div>
                      <div className="text-sm text-gray-500">{usuario.email}</div>
                    </div>
                  </label>
                ))}
              {usuariosFiltrados.filter(u => !membrosSelecionados.includes(u.id)).length === 0 && (
                <p className="text-sm text-gray-400 italic p-3">Todos os usuários já são membros.</p>
              )}
            </div>
          </div>
        </form>

        <div className="p-6 border-t border-gray-200 flex justify-between">
          <button
            type="button"
            onClick={handleRemover}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
            disabled={salvando}
          >
            Remover Canal
          </button>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              disabled={salvando}
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={!nome.trim() || membrosSelecionados.length === 0 || salvando}
              className="px-4 py-2 bg-bravo-primary hover:bg-blue-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {salvando ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={modalConfirmacaoAberto}
        title="Remover Canal"
        message="Tem certeza que deseja remover este canal? Esta ação não pode ser desfeita."
        confirmText="Remover"
        onConfirm={confirmarRemocao}
        onCancel={() => setModalConfirmacaoAberto(false)}
      />
    </div>
  );
};

export default ModalEditarCanal;
